import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Lock, Sparkles } from "lucide-react";
import { fmtINR, IDEA_STAGES, stageMeta } from "./bizdevUtils";
import IdeaDialog from "./IdeaDialog";

/** Iter 169 · Ideas pipeline board — kanban by stage with quick-add. */

const BOARD_STAGES = IDEA_STAGES.filter((s) => s.key !== "killed");

export default function IdeasBoard({ wsId }) {
  const [ideas, setIdeas] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [q, setQ] = useState("");
  const [ventureFilter, setVentureFilter] = useState("");
  const [showKilled, setShowKilled] = useState(false);
  const [editing, setEditing] = useState(null); // idea object or "new:<stage>"
  const [quickAdd, setQuickAdd] = useState({}); // stage -> text

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (ventureFilter) params.set("venture_id", ventureFilter);
      const [i, v] = await Promise.all([
        api.get(`/workspaces/${wsId}/bizdev/ideas?${params.toString()}`),
        api.get(`/workspaces/${wsId}/bizdev/ventures`),
      ]);
      setIdeas(i.data.rows || []);
      setVentures(v.data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load ideas")); }
  }, [wsId, q, ventureFilter]);
  useEffect(() => { load(); }, [load]);

  const quickCreate = async (stage) => {
    const title = (quickAdd[stage] || "").trim();
    if (!title) return;
    try {
      await api.post(`/workspaces/${wsId}/bizdev/ideas`, { title, stage });
      setQuickAdd((s) => ({ ...s, [stage]: "" }));
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to add idea")); }
  };

  const columns = (showKilled ? IDEA_STAGES : BOARD_STAGES);
  const byStage = (k) => ideas.filter((i) => i.stage === k);

  return (
    <div className="space-y-3" data-testid="bizdev-ideas-board">
      <div className="flex items-center gap-2 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search ideas, tags…"
               className="h-8 text-xs w-56" data-testid="ideas-search-input" />
        <Select value={ventureFilter || "_all"}
                onValueChange={(v) => setVentureFilter(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="ideas-venture-filter">
            <SelectValue placeholder="Venture" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All ventures</SelectItem>
            {ventures.map((v) => (
              <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button type="button" onClick={() => setShowKilled((s) => !s)}
                className={`text-[11px] uppercase tracking-widest border px-2 py-1 ${showKilled ? "bg-foreground text-background" : "text-muted-foreground"}`}
                data-testid="ideas-toggle-killed">
          {showKilled ? "Hide killed" : "Show killed"}
        </button>
        <div className="ml-auto">
          <Button size="sm" className="h-8 text-xs" onClick={() => setEditing("new:spark")}
                  data-testid="ideas-new-btn">
            <Plus className="w-3 h-3 mr-1" /> New idea
          </Button>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2">
        {columns.map((s) => {
          const cards = byStage(s.key);
          const upside = cards.reduce((a, i) => a + (i.weighted_monthly || 0), 0);
          return (
            <div key={s.key} className="min-w-[230px] w-[230px] flex-shrink-0"
                 data-testid={`ideas-col-${s.key}`}>
              <div className={`flex items-center gap-1.5 border px-2 py-1.5 ${s.color}`}>
                <span className="text-[10px] uppercase tracking-widest font-semibold">{s.label}</span>
                <span className="text-[10px] font-mono">({cards.length})</span>
                {upside > 0 && (
                  <span className="ml-auto text-[10px] font-mono">{fmtINR(upside)}/mo</span>
                )}
              </div>
              <div className="space-y-1.5 mt-1.5">
                {cards.map((i) => (
                  <button key={i.id} type="button" onClick={() => setEditing(i)}
                          className="w-full text-left border border-border bg-card hover:border-primary/60 transition-colors p-2.5"
                          data-testid={`idea-card-${i.id}`}>
                    <div className="flex items-start gap-1">
                      <span className="text-xs font-medium leading-snug flex-1">{i.title}</span>
                      {i.private && <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                    </div>
                    {(i.tags?.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`idea-card-tags-${i.id}`}>
                        {i.tags.slice(0, 4).map((t) => (
                          <span key={t}
                                className={`text-[9px] uppercase tracking-wider px-1 py-0.5 border ${t === "resurrected" ? "border-violet-300 bg-violet-50 text-violet-700" : "border-border bg-muted/50 text-muted-foreground"}`}
                                data-testid={`idea-tag-${t}`}>
                            {t === "resurrected" ? "🧟 revived" : t}
                          </span>
                        ))}
                        {i.tags.length > 4 && (
                          <span className="text-[9px] text-muted-foreground">+{i.tags.length - 4}</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground border border-border px-1 py-0.5">
                        {(i.category || "").replace("_", " ")}
                      </span>
                      {i.weighted_monthly > 0 && (
                        <span className="text-[10px] font-mono text-emerald-700">
                          {fmtINR(i.weighted_monthly)}/mo
                        </span>
                      )}
                      {i.weighted_one_time > 0 && (
                        <span className="text-[10px] font-mono text-emerald-700">
                          {fmtINR(i.weighted_one_time)} once
                        </span>
                      )}
                      <span className="ml-auto text-[10px] font-mono text-primary font-bold"
                            title={`ICE ${i.ice_score}`}>
                        R {i.rice_score}
                      </span>
                    </div>
                    {i.venture_name && (
                      <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> {i.venture_name}
                      </div>
                    )}
                  </button>
                ))}
                <div className="flex gap-1">
                  <Input value={quickAdd[s.key] || ""}
                         onChange={(e) => setQuickAdd((st) => ({ ...st, [s.key]: e.target.value }))}
                         onKeyDown={(e) => { if (e.key === "Enter") quickCreate(s.key); }}
                         placeholder="+ quick add"
                         className="h-7 text-[11px]"
                         data-testid={`ideas-quickadd-${s.key}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <IdeaDialog
          wsId={wsId}
          idea={typeof editing === "string" ? null : editing}
          initialStage={typeof editing === "string" ? editing.split(":")[1] : undefined}
          ventures={ventures}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
